<script lang="ts">
  // Overview > Pulse — the org-wide snapshot for one day (stat tiles +
  // who's-in/who's-out/who's-missing/who's-on-leave + pending-by-type).
  // Split out of the old OverviewPage.svelte; the per-site breakdown now
  // lives in its own tab (OverviewBySite.svelte) per the mockup's
  // Pulse/By site/Stats tab split.
  //
  // The day is picked by OverviewDateNav and sent as ?date= — see
  // hr_overview.go for which members are date-scoped and which are "right
  // now" facts. On a past day the server returns the live blocks empty, and
  // this view hides their tiles rather than rendering a permanent zero that
  // reads like "nobody was in".
  import { createQuery } from '@tanstack/svelte-query';
  import { ChevronDown } from 'lucide-svelte';
  import { hrOverviewApi, hrAttendanceReportApi } from '$lib/api/hr';
  import { fieldApi } from '$lib/api/field';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import InsightCard from '$lib/components/ui/InsightCard.svelte';
  import BarRow from '$lib/components/ui/BarRow.svelte';
  import OverviewDateNav from './OverviewDateNav.svelte';
  import OverviewDayRoster from './OverviewDayRoster.svelte';
  import { todayISO } from '$lib/utils/appDate';

  let selectedDate = $state(todayISO());
  const viewingToday = $derived(selectedDate === todayISO());

  const query = createQuery(() => ({
    queryKey: [session.isFieldScoped ? 'field-overview' : 'hr-overview', selectedDate],
    queryFn: () => session.isFieldScoped ? fieldApi.overview(selectedDate) : hrOverviewApi.get(selectedDate),
    // A past day is settled history — polling it would just re-fetch the
    // same payload every minute.
    refetchInterval: viewingToday ? 60_000 : false
  }));

  function label(key: string): string {
    return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
  }

  // "12" means nothing without the denominator; the tiles' hint lines quote
  // every headcount as a share of the active roll.
  function share(count: number): string {
    const total = query.data?.active_employee_count ?? 0;
    return total > 0 ? `${Math.round((count / total) * 100)}%` : '—';
  }

  type Tone = 'good' | 'warn' | 'bad' | 'idle';

  async function downloadLateReport() {
    try {
      await hrAttendanceReportApi.downloadLateReport(selectedDate, selectedDate);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }
</script>

<div class="tym-page">
  <OverviewDateNav date={selectedDate} onChange={(d) => (selectedDate = d)} />

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load the org overview.</div>
  {:else if query.data}
    {@const o = query.data}
    <!-- Trust the server's resolved date over the local one: it clamps a
         future ?date= and decides the calendar day in APP_TIMEZONE. -->
    {@const isToday = o.date === todayISO()}
    {@const day = isToday ? 'today' : 'that day'}
    {@const missing = o.not_punched_in_today.users ?? []}
    {@const chase = missing.filter((u) => !u.note)}
    {@const kinds = [
      { label: 'Full day', block: o.on_leave_today },
      { label: 'Half day', block: o.half_day_today },
      { label: 'Late', block: o.late_today },
      { label: 'Early out', block: o.early_leaving_today }
    ]}
    <!-- The hint line under each number is the point of the strip: "4" says
         nothing on its own, "4 — 31% of active employees" is something you
         can act on. Every hint is derived from the same payload, never a
         guess about what the number ought to be. -->
    {@const tiles = [
      {
        label: 'Active employees',
        value: o.active_employee_count,
        hint: `${o.remote_employee_count} hotel field technicians or sales`
      },
      ...(isToday
        ? [
            { label: 'Hotel / sales', value: o.remote_employee_count, hint: 'Not required to punch at a geofenced site' },
            {
              label: 'Punched in now',
              value: o.punched_in_now.count,
              tone: (o.punched_in_now.count > 0 ? 'good' : 'idle') as Tone,
              pulse: o.punched_in_now.count > 0,
              hint:
                o.punched_in_now.count > 0 ? `${share(o.punched_in_now.count)} of active employees` : 'Nobody is on the clock'
            }
          ]
        : []),
      {
        label: `Punched out ${day}`,
        value: o.punched_out_today.count,
        hint: o.punched_out_today.count > 0 ? `${share(o.punched_out_today.count)} of active employees` : 'No one has left yet'
      },
      {
        label: `Not punched in ${day}`,
        value: chase.length,
        tone: (chase.length > 0 ? 'warn' : 'good') as Tone,
        hint:
          chase.length > 0
            ? `${share(chase.length)} of active employees`
            : missing.length > 0
              ? 'Everyone else is on approved leave'
              : 'Everyone active has punched in'
      },
      {
        label: `On leave ${day}`,
        value: o.on_leave_today.count,
        hint: o.on_leave_today.count > 0 ? 'Full-day approved leave' : 'No full-day leave'
      },
      ...(o.half_day_today.count > 0
        ? [{ label: `Half day ${day}`, value: o.half_day_today.count, hint: 'Approved half day' }]
        : []),
      ...(o.late_today.count > 0 ? [{ label: `Late ${day}`, value: o.late_today.count, hint: 'Approved late coming' }] : []),
      ...(o.early_leaving_today.count > 0
        ? [{ label: `Early out ${day}`, value: o.early_leaving_today.count, hint: 'Approved early departure' }]
        : []),
      {
        label: 'Pending requests',
        value: o.pending_requests.count,
        tone: (o.pending_requests.count > 0 ? 'warn' : 'good') as Tone,
        hint: o.pending_requests.count > 0 ? 'Waiting for a decision in Inbox' : 'Inbox is clear'
      }
    ]}

    <div
      class="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-7"
      data-testid="overview-tiles"
    >
      {#each tiles as t (t.label)}
        <InsightCard label={t.label} value={t.value} hint={t.hint} tone={t.tone} pulse={t.pulse} />
      {/each}
    </div>

    <!-- The day roster replaces the old "Punched in now" and "Punched out
         today" disclosures: a still-in row answers both, and unlike either
         list it keeps the person who punched out at lunch and came back —
         who used to fall between the two. Grouped by site so two people
         still in at Hoskote and Office don't read as one pile. The tiles
         above still carry the headline counts. -->
    <div class="mb-4">
      <div class="mb-2 flex items-baseline justify-between gap-3">
        <p class="tym-section-title text-base">Roster — {isToday ? 'today' : o.date} ({o.day_roster.count})</p>
        <div class="flex items-center gap-3"><p class="tym-section-sub !mt-0">Grouped by site</p>{#if !session.isFieldScoped}<button type="button" class="btn-secondary text-[12px]" onclick={downloadLateReport}>Download late CSV</button>{/if}</div>
      </div>
      <OverviewDayRoster rows={o.day_roster.users ?? []} {isToday} />
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <details class="tym-disclosure tym-card p-5" open>
        <summary class="flex cursor-pointer items-center justify-between gap-2">
          <span class="tym-section-title text-base">Not punched in {day} ({chase.length})</span>
          <ChevronDown class="tym-disclosure-chevron h-4 w-4 shrink-0 text-dark-gray" />
        </summary>
        <ul class="mt-3.5 space-y-2.5 text-[13px]">
          {#each missing as u (u.user_id)}
            <li class="border-b border-toyama-cloudy-gray/60 pb-2.5 text-dark-gray last:border-none last:pb-0">
              {u.name}{#if u.note} ({u.note}){/if}
            </li>
          {:else}
            <li class="tym-empty-state !p-0 text-left">Everyone active has punched in — nothing to chase.</li>
          {/each}
        </ul>
      </details>

      <details class="tym-disclosure tym-card p-5" open>
        <summary class="flex cursor-pointer items-center justify-between gap-2">
          <span class="tym-section-title text-base">Approved attendance notes</span>
          <ChevronDown class="tym-disclosure-chevron h-4 w-4 shrink-0 text-dark-gray" />
        </summary>
        <div class="mt-3.5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {#each kinds as kind (kind.label)}
            <section class="min-w-0 rounded-lg border border-toyama-cloudy-gray/70 p-3">
              <p class="text-[12px] font-semibold uppercase tracking-[.08em] text-navy">{kind.label} ({kind.block.count})</p>
              <ul class="mt-2 space-y-2 text-[13px]">
                {#each kind.block.users as u (u.user_id)}
                  <li class="min-w-0 border-b border-toyama-cloudy-gray/60 pb-2 text-dark-gray last:border-none last:pb-0">
                    <span class="font-medium text-navy">{u.name}</span>
                    <span class="ml-1">· {u.note}</span>
                  </li>
                {:else}
                  <li class="text-dark-gray">None {day}.</li>
                {/each}
              </ul>
            </section>
          {/each}
        </div>
      </details>
    </div>

    {#if o.pending_requests.by_type.length > 0}
      {@const max = Math.max(...o.pending_requests.by_type.map((r) => r.count))}
      <div class="tym-card mt-4 p-5">
        <p class="tym-section-title mb-3.5 text-base">Pending requests by type</p>
        <div class="space-y-2.5">
          {#each o.pending_requests.by_type as row (row.key)}
            <BarRow label={label(row.key)} value={row.count} {max} />
          {/each}
        </div>
      </div>
    {/if}
  {/if}
</div>
